import intl from "react-intl-universal";
import {WorkspaceModelType} from "../../stores/WorkspaceStore";
import {
    updateWorkspaceArchivedIdsAdd,
    updateWorkspaceChildIdsAdd, updateWorkspaceChildIdsRemove,
    updateWorkspaceTrashedIdsAdd
} from "../workspace/updateWorkspace";
import {UndoableAction} from "../common/UndoableAction";
import {undoableFunctionCall} from "../common/undoableFunctionCall";
import {UndoableActionGroup} from "../common/UndoableActionGroup";
import {logErrorToSentry} from "../../utils/errors/logErrorToSentry";
import {updateItemDB} from "./updateItem";
import { CrudServices } from "../../platform/services/types/ServiceDependencies";
import { DataSourceManager } from "../../stores/dataSources/DataSourceManager";


export class UndoablePin implements UndoableAction {
    private services: CrudServices;
    private dsManager: DataSourceManager;
    itemId: string;
    workspaceMST: WorkspaceModelType;
    constructor(
        services: CrudServices,
        dsManager: DataSourceManager,
        itemId: string, 
        workspaceMST: WorkspaceModelType
    ) {
        this.services = services;
        this.dsManager = dsManager;
        this.itemId = itemId;
        this.workspaceMST = workspaceMST;
    }
    execute(): Promise<any> {
        return pinItem(this.services, this.dsManager, this.itemId, this.workspaceMST);
    }
    undo(): Promise<any> {
        return unPinItem(this.services, this.dsManager, this.itemId, this.workspaceMST);
    }
}

export class UndoableUnPin implements UndoableAction {
    private services: CrudServices;
    private dsManager: DataSourceManager;
    itemId: string;
    workspaceMST: WorkspaceModelType;
    willAddToAnotherPlace: boolean;
    constructor(
        services: CrudServices,
        dsManager: DataSourceManager,
        itemId: string, 
        workspaceMST: WorkspaceModelType, 
        willAddToAnotherPlace: boolean = false
    ) {
        this.services = services;
        this.dsManager = dsManager;
        this.itemId = itemId;
        this.workspaceMST = workspaceMST;
        this.willAddToAnotherPlace = willAddToAnotherPlace;
    }
    execute(): Promise<any> {
        return unPinItem(this.services, this.dsManager, this.itemId, this.workspaceMST, this.willAddToAnotherPlace);
    }
    undo(): Promise<any> {
        return pinItem(this.services, this.dsManager, this.itemId, this.workspaceMST);
    }
}

export const pinItemUndoable = (
    services: CrudServices,
    dsManager: DataSourceManager,
    itemId: string, 
    workspace: WorkspaceModelType
) => {
    const undoableAction = new UndoablePin(services, dsManager, itemId, workspace);
    undoableFunctionCall(undoableAction, intl.get('snackbar.pin_item_success').d('Page pinned')).catch(logErrorToSentry);
}

export const unPinItemUndoable = (
    services: CrudServices,
    dsManager: DataSourceManager,
    itemId: string, 
    workspace: WorkspaceModelType
) => {
    const undoableAction = new UndoableUnPin(services, dsManager, itemId, workspace);
    undoableFunctionCall(undoableAction, intl.get('snackbar.unpin_item_success').d('Page unpinned')).catch(logErrorToSentry);
}

export function pinItem(
    services: CrudServices,
    dsManager: DataSourceManager,
    itemId: string, 
    workspace: WorkspaceModelType, 
    undoableGroup?: UndoableActionGroup
) {
    if (undoableGroup) {
        const undoableAction = new UndoablePin(services, dsManager, itemId, workspace);
        return undoableGroup.add(undoableAction);
    } else {
        return _pinItem(services, dsManager, itemId, workspace);
    }
}

export function _pinItem(
    services: CrudServices,
    dsManager: DataSourceManager,
    itemId: string, 
    workspace: WorkspaceModelType
) {
    const wsItem = workspace.items.get(itemId)!;
    wsItem.setPinned(true);
    return dbChangePin(services, dsManager, workspace.id, itemId, true);
}

export async function unPinItem(
    services: CrudServices,
    dsManager: DataSourceManager,
    itemId: string, 
    workspace: WorkspaceModelType, 
    willAddToAnotherPlace: boolean = false, 
    undoableGroup?: UndoableActionGroup
) {
    if (undoableGroup !== undefined) {
        const undoableAction = new UndoableUnPin(services, dsManager, itemId, workspace, willAddToAnotherPlace);
        return undoableGroup.add(undoableAction);
    } else {
        return _unPinItem(services, dsManager, itemId, workspace, willAddToAnotherPlace);
    }
}
export async function _unPinItem(
    services: CrudServices,
    dsManager: DataSourceManager,
    itemId: string, 
    workspace: WorkspaceModelType, 
    willAddToAnotherPlace: boolean = false
) {
    const wsUpdateServices = {...services, dsManager};
    let affectedDBChange = Promise.resolve();
    let workspaceChangeWait = Promise.resolve();
    const wsItem = workspace.items.get(itemId)!;
    wsItem.setPinned(false);
    /* d&d may lead to the state when pinned item is not present in any workspace level arrays */
    if (willAddToAnotherPlace && wsItem.parentIds.length === 0) {
        workspaceChangeWait = updateWorkspaceChildIdsRemove(wsUpdateServices, workspace, itemId);
    } else if (!willAddToAnotherPlace && wsItem.parentIds.length === 0) {
        if (wsItem.trashed) {
            affectedDBChange = updateWorkspaceTrashedIdsAdd(wsUpdateServices, workspace, itemId);
        } else if (wsItem.archived) {
            affectedDBChange = updateWorkspaceArchivedIdsAdd(wsUpdateServices, workspace, itemId);
        } else {
            if (!workspace.childIds.includes(itemId)) {
                affectedDBChange = updateWorkspaceChildIdsAdd(wsUpdateServices, workspace, itemId);
            }
        }
    }
    return Promise.all([dbChangePin(services, dsManager, workspace.id, itemId, false), affectedDBChange, workspaceChangeWait]);
}

async function dbChangePin(
    services: CrudServices,
    dsManager: DataSourceManager,
    wsId: string,
    itemId: string,
    newValue: boolean
) {
    return updateItemDB(services, dsManager, wsId, itemId, {
        pinned: newValue,
    })
}